// src/pages/Mafia/MafiaAdmin.js
//
// IT Admin screen for managing Mafia Aux permissions.
//
// v2: EmpId-based. Admins pick an employee by name from a searchable dropdown;
// the row stores the EmpId in MafiaAuxPermissions.EmpId. No "Windows username"
// concept exposed in the UI.
//
// Access: rendered as a sub-screen of MenuPage only when whoami returns
// isAdmin: true. Every endpoint it calls is independently gated server-side
// with [Authorize(Roles="IT Admin")].

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";

export default function MafiaAdmin({ onBack }) {
  const dialog = useAppDialog();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [levels, setLevels] = useState([]);

  // Add-permission form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newLevel, setNewLevel] = useState(0);
  const [saving, setSaving] = useState(false);

  // Per-row edit state: { [id]: levelValue }
  const [rowEdits, setRowEdits] = useState({});

  const levelLabel = useCallback(
    (lvl) => {
      const match = levels.find((l) => Number(l.permissionLevel) === Number(lvl));
      return match ? match.description : `Level ${lvl}`;
    },
    [levels]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [permsRes, levelsRes] = await Promise.all([
        api.get("mafia/admin/permissions"),
        api.get("mafia/admin/permission-levels"),
      ]);
      setPermissions(Array.isArray(permsRes.data) ? permsRes.data : []);
      setLevels(Array.isArray(levelsRes.data) ? levelsRes.data : []);
    } catch (err) {
      console.error("Failed to load Mafia Aux permissions", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoadError(
          "You do not have access to the Mafia Aux permissions admin. This screen is restricted to IT Admin users."
        );
      } else {
        setLoadError("Failed to load Mafia Aux permissions. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const defaultLevelValue = useMemo(() => {
    if (levels.length === 0) return 0;
    const sorted = [...levels].sort(
      (a, b) => Number(a.permissionLevel) - Number(b.permissionLevel)
    );
    return Number(sorted[0].permissionLevel);
  }, [levels]);

  function openAddForm() {
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setEmployees([]);
    setNewLevel(defaultLevelValue);
    setShowAddForm(true);
  }

  function closeAddForm() {
    setShowAddForm(false);
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setEmployees([]);
  }

  async function handleEmployeeSearch() {
    const term = employeeSearch.trim();
    setSearchingEmployees(true);
    try {
      const res = await api.get("mafia/admin/employees", {
        params: { search: term },
      });
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Employee search failed", err);
      await dialog.error("Employee search failed. Please try again.");
    } finally {
      setSearchingEmployees(false);
    }
  }

  async function handleCreate() {
    if (!selectedEmployee) {
      await dialog.warning("Select an employee first.");
      return;
    }
    const empId = (selectedEmployee.empId || "").trim();
    if (!empId) {
      await dialog.warning("Selected employee has no EmpId on file.");
      return;
    }

    setSaving(true);
    try {
      await api.post("mafia/admin/permissions", {
        empId,
        accessLevel: Number(newLevel),
      });
      await dialog.info(
        `Granted "${levelLabel(newLevel)}" access to ${selectedEmployee.name} (${empId}).`
      );
      closeAddForm();
      await loadAll();
    } catch (err) {
      console.error("Create permission failed", err);
      const status = err?.response?.status;
      if (status === 409) {
        await dialog.warning(
          `${selectedEmployee.name} (${empId}) already has a Mafia Aux permission row. Edit the existing row instead.`
        );
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can grant Mafia Aux access.");
      } else {
        await dialog.error("Failed to grant access. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  function startRowEdit(row) {
    setRowEdits((prev) => ({ ...prev, [row.id]: Number(row.accessLevel) }));
  }

  function cancelRowEdit(id) {
    setRowEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setRowEditValue(id, value) {
    setRowEdits((prev) => ({ ...prev, [id]: Number(value) }));
  }

  async function handleUpdate(row) {
    const newValue = rowEdits[row.id];
    if (newValue === undefined || Number(newValue) === Number(row.accessLevel)) {
      cancelRowEdit(row.id);
      return;
    }
    try {
      await api.put(`mafia/admin/permissions/${row.id}`, {
        empId: row.empId,
        accessLevel: Number(newValue),
      });
      await dialog.info(
        `Updated ${row.employeeName || row.empId} to "${levelLabel(newValue)}".`
      );
      cancelRowEdit(row.id);
      await loadAll();
    } catch (err) {
      console.error("Update permission failed", err);
      const status = err?.response?.status;
      if (status === 404) {
        await dialog.warning("That permission row no longer exists. Refreshing.");
        cancelRowEdit(row.id);
        await loadAll();
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can change Mafia Aux access.");
      } else {
        await dialog.error("Failed to update access. Please try again.");
      }
    }
  }

  async function handleDelete(row) {
    const who = row.employeeName ? `${row.employeeName} (${row.empId})` : row.empId;
    const confirmed = await dialog.confirm(
      `Remove Mafia Aux access for ${who}? They will become non-credentialed.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`mafia/admin/permissions/${row.id}`);
      await dialog.info(`Removed Mafia Aux access for ${who}.`);
      await loadAll();
    } catch (err) {
      console.error("Delete permission failed", err);
      const status = err?.response?.status;
      if (status === 404) {
        await dialog.warning("That permission row no longer exists. Refreshing.");
        await loadAll();
      } else if (status === 401 || status === 403) {
        await dialog.error("Only IT Admin users can remove Mafia Aux access.");
      } else {
        await dialog.error("Failed to remove access. Please try again.");
      }
    }
  }

  function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  }

  return (
    <Container fluid className="p-3 mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-shield-user-line"
        eyebrow="MAFIA Aux"
        title="Permissions Admin"
        subtitle="Grant, change, or remove Mafia Aux access by employee. IT Admin only."
        tone="violet"
        actions={
          <>
            {typeof onBack === "function" && (
              <Button
                color="light"
                onClick={onBack}
                style={{
                  background: "rgba(255,255,255,0.92)",
                  border: "none",
                  fontWeight: 500,
                }}
              >
                <i className="ri-arrow-left-line align-bottom me-1" />
                Back to Menu
              </Button>
            )}
            <Button
              color="light"
              onClick={loadAll}
              disabled={loading}
              style={{
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                fontWeight: 500,
              }}
            >
              <i className="ri-refresh-line align-bottom me-1" />
              Refresh
            </Button>
          </>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Permissions Admin" },
        ]}
      />

      {loadError ? (
        <Alert color="danger">{loadError}</Alert>
      ) : loading ? (
        <div className="text-center p-5">
          <Spinner /> <span className="ms-2">Loading permissions…</span>
        </div>
      ) : (
        <>
          {/* Add permission */}
          <Card className="mb-3">
            <CardBody>
              {!showAddForm ? (
                <Button color="primary" onClick={openAddForm}>
                  <i className="ri-add-line align-bottom" /> Grant Access
                </Button>
              ) : (
                <div>
                  <h6 className="mb-3">Grant Mafia Aux access</h6>
                  <Row className="g-2 align-items-end">
                    <Col md={5}>
                      <label className="form-label mb-1">
                        Find employee (name or EmpId)
                      </label>
                      <div className="d-flex gap-2">
                        <Input
                          value={employeeSearch}
                          placeholder="e.g. Sharma or 3325"
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleEmployeeSearch();
                          }}
                        />
                        <Button
                          color="secondary"
                          onClick={handleEmployeeSearch}
                          disabled={searchingEmployees}
                        >
                          {searchingEmployees ? <Spinner size="sm" /> : "Search"}
                        </Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <label className="form-label mb-1">Employee</label>
                      <Input
                        type="select"
                        value={selectedEmployee ? selectedEmployee.empId : ""}
                        onChange={(e) => {
                          const emp = employees.find(
                            (x) => x.empId === e.target.value
                          );
                          setSelectedEmployee(emp || null);
                        }}
                      >
                        <option value="">
                          {employees.length === 0
                            ? "— search first —"
                            : "— select employee —"}
                        </option>
                        {employees.map((emp) => (
                          <option key={emp.empId} value={emp.empId}>
                            {emp.name} ({emp.empId})
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <label className="form-label mb-1">Access level</label>
                      <Input
                        type="select"
                        value={newLevel}
                        onChange={(e) => setNewLevel(Number(e.target.value))}
                      >
                        {levels
                          .slice()
                          .sort(
                            (a, b) =>
                              Number(a.permissionLevel) -
                              Number(b.permissionLevel)
                          )
                          .map((l) => (
                            <option
                              key={l.permissionLevel}
                              value={l.permissionLevel}
                            >
                              {l.permissionLevel} — {l.description}
                            </option>
                          ))}
                      </Input>
                    </Col>
                  </Row>
                  <div className="mt-3 d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={handleCreate}
                      disabled={saving || !selectedEmployee}
                    >
                      {saving ? <Spinner size="sm" /> : "Save"}
                    </Button>
                    <Button color="light" onClick={closeAddForm} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Permissions table */}
          <Card>
            <CardBody>
              <h6 className="mb-3">
                Current permissions{" "}
                <Badge color="light" className="text-dark">
                  {permissions.length}
                </Badge>
              </h6>
              {permissions.length === 0 ? (
                <p className="text-muted mb-0">
                  No Mafia Aux permissions have been granted yet.
                </p>
              ) : (
                <Table responsive hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>EmpId</th>
                      <th>Access Level</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((row) => {
                      const editing = rowEdits[row.id] !== undefined;
                      return (
                        <tr key={row.id}>
                          <td>{row.employeeName || <em className="text-muted">unknown</em>}</td>
                          <td><code>{row.empId}</code></td>
                          <td>
                            {editing ? (
                              <Input
                                type="select"
                                bsSize="sm"
                                style={{ maxWidth: 220 }}
                                value={rowEdits[row.id]}
                                onChange={(e) =>
                                  setRowEditValue(row.id, e.target.value)
                                }
                              >
                                {levels
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      Number(a.permissionLevel) -
                                      Number(b.permissionLevel)
                                  )
                                  .map((l) => (
                                    <option
                                      key={l.permissionLevel}
                                      value={l.permissionLevel}
                                    >
                                      {l.permissionLevel} — {l.description}
                                    </option>
                                  ))}
                              </Input>
                            ) : (
                              <>
                                <Badge color="info" className="me-2">
                                  {row.accessLevel}
                                </Badge>
                                {levelLabel(row.accessLevel)}
                              </>
                            )}
                          </td>
                          <td>
                            <small>
                              {formatDate(row.dateCreated)}
                              {row.createdBy ? (
                                <span className="text-muted">
                                  {" "}
                                  by {row.createdBy}
                                </span>
                              ) : null}
                            </small>
                          </td>
                          <td>
                            <small>
                              {formatDate(row.dateUpdated)}
                              {row.updatedBy ? (
                                <span className="text-muted">
                                  {" "}
                                  by {row.updatedBy}
                                </span>
                              ) : null}
                            </small>
                          </td>
                          <td className="text-end">
                            {editing ? (
                              <>
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleUpdate(row)}
                                >
                                  Save
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => cancelRowEdit(row.id)}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => startRowEdit(row)}
                                >
                                  <i className="ri-pencil-line align-bottom" />{" "}
                                  Edit
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(row)}
                                >
                                  <i className="ri-delete-bin-line align-bottom" />{" "}
                                  Remove
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </Container>
  );
}
